import style from './index.module.less';
import SwitchIcon from '@/assets/icon/switch.svg';
import { useEffect, useState, useRef } from 'react';
import { createAiToken, getAppConfig, getMessageList, getPersonaList, userBindRole } from '@/app/user';
import RtcClient from '@/lib/RtcClient';

import Config from '@/config';
import { useJoin, useLeave } from '@/lib/useCommon';
import { useDispatch } from 'react-redux';
import { clearCurrentMsg, loadHistoryMsg } from '@/store/slices/room';
import { useLocation } from 'react-router-dom';
import { useAppContent } from '@/context/AppContent';
import Utils from '@/utils/utils';

interface Persona {
  id: number;
  user_id: number;
  persona_id: number;
  persona: PersonaInfo;
  talk: Talk;
}

interface PersonaInfo {
  id: number;
  name: string;
  image: string;
  param_text: string;
  image_text: string;
}

interface Talk {
  id: number;
  peosona_id: number;
  text: string;
}

function RoleSwitch() {
  const [showPopup, setShowPopup] = useState(false);
  const [roleList, setRoleList] = useState<Persona[]>([]);
  const [currentRole, setCurrentRole] = useState<Persona | null>(null);
  const [joining, dispatchJoin] = useJoin();
  const initialized = useRef(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const leave = useLeave();
  const { switchRole, sendMessage } = useAppContent();

  // 统一处理角色初始化逻辑
  useEffect(() => {
    const initializeRole = async () => {
      const checkRoleId = sessionStorage.getItem('check-role-id');
      const urlParams = new URLSearchParams(location.search);
      const urlRoleId = urlParams.get('roleId');
      const roleIdToCheck = urlRoleId || checkRoleId;

      try {
        // 如果有角色ID参数，尝试绑定
        let bindSuccess = false;
        if (roleIdToCheck) {
          console.log('检测到角色参数，尝试绑定: ', roleIdToCheck);
          try {
            await userBindRole(roleIdToCheck);
            bindSuccess = true;
            console.log('角色绑定成功');
          } catch (bindError) {
            console.error('角色绑定失败:', bindError);
          } finally {
            sessionStorage.removeItem('check-role-id');
          }
        }

        // 获取角色列表
        const roleListResponse = await getPersonaList();
        const roles = roleListResponse as Persona[];
        setRoleList(roles);

        if (roles && roles.length > 0) {
          // 如果绑定成功且能找到对应角色，使用该角色
          if (bindSuccess && roleIdToCheck) {
            const targetRole = roles.find((role) => role.persona_id === Number(roleIdToCheck));
            if (targetRole) {
              await handleSwitchRole(targetRole);
              // sendMessage(targetRole.talk.text, '请复读这个话');
              return;
            }
          }

          // 如果绑定失败或找不到对应角色，使用第一个角色
          await handleSwitchRole(roles[0]);
        }
      } catch (error) {
        console.error('Failed to initialize role:', error);
      }
    };

    if (!initialized.current) {
      initialized.current = true;
      initializeRole();
    }

    return () => {
      setCurrentRole(null);
      setRoleList([]);
      initialized.current = false;
    };
  }, [location.search]); // 当 URL 参数变化时重新执行

  const handleSwitchRole = async (role: Persona) => {

    setCurrentRole(role);
    setShowPopup(false);
    let history = '';
    // 获取聊天记录
    localStorage.setItem('roleId', String(role.persona_id));

    let messageList: any[] = (await getMessageList(role.persona_id)) || [];
    messageList = messageList.map((message) => ({
      recordId: message.id || Utils.generateUUID(),
      ...message,
    }));

    // console.log('获取到聊天记录: ', messageList);

    if (Array.isArray(messageList) && messageList.length > 0) {
      history = messageList
        .reverse()
        .map((item: any) => item.value)
        .join('\n');
      dispatch(loadHistoryMsg(messageList));
    }

    // 设置机器人id
    Config.BotName = `C-${role.persona_id}`;
    // 设置人设
    Config.Prompt = `
      ${role?.persona?.param_text}
## 我们之前的聊天记录
### 注意点
聊天记录里可能包含你给我讲的故事，请注意甄别
### 记录内容
${history}
`;
    // 设置打招呼, 如果没有聊天记录，则每次都打招呼，如果有，则不打
    Config.WelcomeSpeech = messageList.length == 0 ? (role?.talk?.text || '你好呀') : '';
    // 设置音色
    // Config.VoiceType = Voice[SCENE.VIRTUAL_GIRL_FRIEND]
    // 重新加载房间
    if (RtcClient.getAudioBotEnabled()) {
      // 清空之前的记录
      await leave()
    }
    // 获取新的token信息，然后加入新房间
    if (!joining) {
      try {
        const res = await createAiToken();
        // @ts-ignore
        await dispatchJoin(
          {
            username: res.user_id,
            roomId: res.room_id,
            token: res.token,
            publishAudio: false,
          }
        );

        // dispatch(loadHistoryMsg(messageList));
        switchRole();
      } catch (error) {
        console.error('Failed to switch role:', error);
      }
    }
  };

  return (
    <div className={style.RoleSwitchWrapper}>
      {currentRole ? (
        <>
          <div className={style.RoleSwitch} onClick={() => setShowPopup(!showPopup)}>
            <div className={style.FlexBox}>
              <img src={currentRole?.persona?.image_text} alt="" className={style.robotAvatar} />
              <span className={style.robotName}>{currentRole?.persona?.name}</span>
            </div>
            <img src={SwitchIcon} alt="" className={style.switchIcon} />
          </div>
          {showPopup && (
            <div className={style.RoleListWrapper}>
              <div className={style.RoleListContainer}>
                {roleList.map((item: Persona, index: number) => (
                  <div className={style.RoleItem} onClick={() => handleSwitchRole(item)} key={index}>
                    <img src={item?.persona?.image_text} alt="" className={style.roleAvatar} />
                    <span className={style.roleName}>{item?.persona?.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={style.RoleSwitch}>
          <span className={style.robotName}>加载中...</span>
        </div>
      )}
    </div>
  );
}

export default RoleSwitch;
